/** @format */

import React from "react";
import { Container as MuiContainer } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  SectionShell,
  RadialGlow,
  FloatingOrb,
  HeaderBlock,
  Badge,
  SectionTitle,
  HighlightText,
  SectionSubtitle,
  StatsStrip,
  StatCell,
  StatNumber,
  StatLabel,
  ContentGrid,
  FounderCard,
  QuoteSymbol,
  NoteLabel,
  NoteText,
  FounderMeta,
  FounderAvatar,
  FounderInfo,
  FounderName,
  FounderTitle,
  SignatureBar,
  TestimonialsColumn,
  TestimonialCard,
  StarRow,
  StarIcon,
  TestimonialQuote,
  TestimonialAuthor,
  AuthorAvatar,
  AuthorInfo,
  AuthorName,
  AuthorRole,
  CtaStrip,
  CtaTagline,
  CtaButton,
} from "./styles";

// ── Component ─────────────────────────────────────────────────────────────────

const SocialProof = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("home");

  const stats = [
    { number: t("socialProof.stat1Number"), label: t("socialProof.stat1Label") },
    { number: t("socialProof.stat2Number"), label: t("socialProof.stat2Label") },
    { number: t("socialProof.stat3Number"), label: t("socialProof.stat3Label") },
    { number: t("socialProof.stat4Number"), label: t("socialProof.stat4Label") },
  ];

  const testimonials = [
    {
      quote: t("socialProof.t1Quote"),
      name: t("socialProof.t1Name"),
      role: t("socialProof.t1Role"),
      tag: t("socialProof.t1Tag"),
      initials: "SA",
      avatarBg: "linear-gradient(135deg, #7c3aed, #a855f7)",
      delay: 100,
    },
    {
      quote: t("socialProof.t2Quote"),
      name: t("socialProof.t2Name"),
      role: t("socialProof.t2Role"),
      tag: t("socialProof.t2Tag"),
      initials: "DO",
      avatarBg: "linear-gradient(135deg, #0284c7, #38bdf8)",
      delay: 220,
    },
    {
      quote: t("socialProof.t3Quote"),
      name: t("socialProof.t3Name"),
      role: t("socialProof.t3Role"),
      tag: t("socialProof.t3Tag"),
      initials: "BH",
      avatarBg: "linear-gradient(135deg, #d97706, #fbbf24)",
      delay: 340,
    },
  ];

  return (
    <SectionShell>
      {/* Ambient radial glow overlay */}
      <RadialGlow />

      {/* Floating glass orbs */}
      <FloatingOrb sx={{ top: "8%", left: "4%", animationDelay: "0s" }} />
      <FloatingOrb sx={{ top: "60%", right: "5%", animationDelay: "3s" }} />
      <FloatingOrb sx={{ bottom: "12%", left: "18%", animationDelay: "6s" }} />

      <MuiContainer maxWidth="xl">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <HeaderBlock data-aos="fade-up" data-aos-duration="800">
          <Badge>{t("socialProof.badge")}</Badge>

          <SectionTitle>
            {t("socialProof.sectionTitle")}{" "}
            <HighlightText>{t("socialProof.sectionTitleHighlight")}</HighlightText>
          </SectionTitle>

          <SectionSubtitle>
            {t("socialProof.sectionSubtitle")}
          </SectionSubtitle>
        </HeaderBlock>

        {/* ── Stats Strip ──────────────────────────────────────────────── */}
        <StatsStrip data-aos="fade-up" data-aos-duration="700" data-aos-delay="100">
          {stats.map((stat, i) => (
            <StatCell key={i} style={{ animationDelay: `${i * 120}ms` }}>
              <StatNumber>{stat.number}</StatNumber>
              <StatLabel>{stat.label}</StatLabel>
            </StatCell>
          ))}
        </StatsStrip>

        {/* ── Two-column: Founder + Testimonials ──────────────────────── */}
        <ContentGrid>
          {/* Founder's Note */}
          <FounderCard data-aos="fade-right" data-aos-duration="800" data-aos-delay="150">
            <QuoteSymbol>"</QuoteSymbol>
            <NoteLabel>{t("socialProof.founderNoteLabel")}</NoteLabel>

            <NoteText style={{ marginBottom: "20px" }}>{t("socialProof.founderPara1")}</NoteText>
            <NoteText style={{ marginBottom: "20px" }}>{t("socialProof.founderPara2")}</NoteText>
            <NoteText style={{ marginBottom: "32px" }}>{t("socialProof.founderPara3")}</NoteText>

            <FounderMeta>
              <FounderAvatar>P</FounderAvatar>
              <FounderInfo>
                <FounderName>{t("socialProof.founderName")}</FounderName>
                <FounderTitle>{t("socialProof.founderTitle")}</FounderTitle>
                <SignatureBar />
              </FounderInfo>
            </FounderMeta>
          </FounderCard>

          {/* Testimonials */}
          <TestimonialsColumn data-aos="fade-left" data-aos-duration="800" data-aos-delay="200">
            {testimonials.map((t, i) => (
              <TestimonialCard key={i} delay={t.delay}>
                {/* Stars */}
                <StarRow>
                  {[0, 1, 2, 3, 4].map((si) => (
                    <StarIcon key={si} i={si}>
                      ★
                    </StarIcon>
                  ))}
                </StarRow>

                <TestimonialQuote>"{t.quote}"</TestimonialQuote>

                <TestimonialAuthor>
                  <AuthorAvatar bg={t.avatarBg}>{t.initials}</AuthorAvatar>
                  <AuthorInfo>
                    <AuthorName>{t.name}</AuthorName>
                    <AuthorRole>{t.role}</AuthorRole>
                  </AuthorInfo>
                  {t.tag && (
                    <span style={{
                      marginLeft: "auto",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase" as const,
                      color: "#1ec195",
                      border: "1px solid rgba(30,193,149,0.45)",
                      borderRadius: "50px",
                      padding: "2px 10px",
                      whiteSpace: "nowrap" as const,
                      flexShrink: 0,
                    }}>{t.tag}</span>
                  )}
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
          </TestimonialsColumn>
        </ContentGrid>

        {/* ── Bottom CTA ───────────────────────────────────────────────── */}
        <CtaStrip data-aos="fade-up" data-aos-duration="700" data-aos-delay="300">
          <CtaTagline>{t("socialProof.ctaTagline")}</CtaTagline>
          <CtaButton onClick={() => navigate("/circle/sign-up")}>
            {t("socialProof.ctaButton")}
          </CtaButton>
        </CtaStrip>
      </MuiContainer>
    </SectionShell>
  );
};

export default SocialProof;
